import React, { useMemo } from 'react';
import {
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import { useDashboardContext } from 'context/dashboard.context';
import { Link } from 'react-router-dom';
import { SidenavGroupEnum, sidenavLinks } from 'router/routes';
import { Logout } from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { getUniqueKey } from 'utils';
import { useAuthContext } from 'context/auth.context';
import Can from 'components/Abilities/Can';
import PermissionCheck from 'components/Abilities/PermissionCheck';
import TenantSelector from '../../components/TenantSelector/TenantSelector';
import { styled } from '@mui/material/styles';
import { hasPermissions } from '../../config/ability';
import BackNavigationLink from 'layout/BackNavigationLink';

const CustomSettingsHeader = styled(Typography)`
  font-weight: 500;
  padding-left: 1rem;
  padding-right: 0.5rem;
  font-size: 1rem;
`;

const SidenavDrawer = (): JSX.Element => {
  const { md, toggleShowSidebar } = useDashboardContext();
  const { logout, ability } = useAuthContext();

  const permittedLinks = useMemo(
    () =>
      sidenavLinks.filter(
        (x) =>
          !x.permission ||
          hasPermissions(
            ability,
            x.permission.action,
            x.permission.subject,
            x.permission.conditions
          )
      ),
    [ability]
  );

  const mainLinks = useMemo(
    () => permittedLinks.filter((x) => x.sidenavGroup === SidenavGroupEnum.Main || !x.sidenavGroup),
    []
  );

  const customSettingsLinks = useMemo(
    () => permittedLinks.filter((x) => x.sidenavGroup === SidenavGroupEnum.CustomSettings),
    []
  );

  return (
    <>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: [1],
        }}
      >
        <IconButton onClick={toggleShowSidebar}>
          <ChevronLeftIcon />
        </IconButton>
      </Toolbar>
      <Divider />
      <List sx={{ display: 'flex', flexDirection: 'column' }}>
        <BackNavigationLink />
        <TenantSelector />
        {mainLinks.map((sideNavLink) => (
          <PermissionCheck key={getUniqueKey(sideNavLink)} permission={sideNavLink.permission}>
            <ListItemButton
              component={Link}
              to={sideNavLink.path}
              selected={location.pathname.includes(sideNavLink.path)}
              onClick={() => !md && toggleShowSidebar()}
            >
              {sideNavLink.icon && <ListItemIcon>{sideNavLink.icon}</ListItemIcon>}
              <ListItemText
                primary={sideNavLink.title}
                primaryTypographyProps={
                  location.pathname.includes(sideNavLink.path) ? { style: { fontWeight: 500 } } : {}
                }
              />
            </ListItemButton>
          </PermissionCheck>
        ))}
        {customSettingsLinks.length > 0 && (
          <>
            {customSettingsLinks.map((sideNavLink) => (
              <PermissionCheck key={getUniqueKey(sideNavLink)} permission={sideNavLink.permission}>
                <ListItemButton
                  component={Link}
                  to={sideNavLink.path}
                  selected={location.pathname.includes(sideNavLink.path)}
                  onClick={() => !md && toggleShowSidebar()}
                >
                  {sideNavLink.icon && <ListItemIcon>{sideNavLink.icon}</ListItemIcon>}
                  <ListItemText
                    primary={sideNavLink.title}
                    primaryTypographyProps={
                      location.pathname.includes(sideNavLink.path)
                        ? { style: { fontWeight: 500 } }
                        : {}
                    }
                  />
                </ListItemButton>
              </PermissionCheck>
            ))}
          </>
        )}
        {!md && (
          <>
            <Divider />
            <ListItemButton onClick={() => logout()}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              <ListItemText primary={'Log Out'} />
            </ListItemButton>
          </>
        )}
      </List>
    </>
  );
};

export default SidenavDrawer;
